import { ArrowLeft, Download, Calendar } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

// Convert YYYY-MM-DD (from <input type="date">) ↔ DD-MM-YYYY (URL format)
export function toInputDate(ddmmyyyy: string): string {
  if (!ddmmyyyy) return ''
  const [d, m, y] = ddmmyyyy.split('-')
  if (!d || !m || !y) return ddmmyyyy
  return `${y}-${m}-${d}`
}

export function toUrlDate(yyyymmdd: string): string {
  if (!yyyymmdd) return ''
  const [y, m, d] = yyyymmdd.split('-')
  if (!d || !m || !y) return yyyymmdd
  return `${d}-${m}-${y}`
}

interface Props {
  campaignName: string
  adUnitName?: string
  dateFrom: string   // YYYY-MM-DD
  dateTo: string     // YYYY-MM-DD
  period: string
  onDateChange: (dateFrom: string, dateTo: string) => void
  onDownloadPdf: () => void
}

export function DashboardHeader({
  campaignName,
  adUnitName,
  dateFrom,
  dateTo,
  period,
  onDateChange,
  onDownloadPdf,
}: Props) {
  const navigate = useNavigate()

  return (
    <div className="sticky top-0 z-20 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4">
      <div className="flex flex-wrap items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors"
          title="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>

        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white truncate">
            {adUnitName ? `${campaignName} — ${adUnitName}` : campaignName}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300">
              {period}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {toUrlDate(dateFrom)} – {toUrlDate(dateTo)}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
              Source: Ad unit
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5">
            <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <input
              type="date"
              value={dateFrom}
              onChange={e => onDateChange(e.target.value, dateTo)}
              className="text-sm bg-transparent text-gray-700 dark:text-gray-300 border-none outline-none w-32"
            />
            <span className="text-gray-400 text-sm">–</span>
            <input
              type="date"
              value={dateTo}
              onChange={e => onDateChange(dateFrom, e.target.value)}
              className="text-sm bg-transparent text-gray-700 dark:text-gray-300 border-none outline-none w-32"
            />
          </div>

          <button
            onClick={onDownloadPdf}
            className="flex items-center gap-2 px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Download className="h-4 w-4" />
            Download PDF
          </button>
        </div>
      </div>
    </div>
  )
}
